import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) {
      const dt = new Date(ms);
      return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
    }
  }
  const p = new Date(d);
  if (isNaN(p)) return '-';
  return `${String(p.getDate()).padStart(2, '0')}/${String(p.getMonth() + 1).padStart(2, '0')}/${p.getFullYear()}`;
};
const fmt2Date = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT = '#e8eaf6';
const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};
const STATUS_COLORS = {
  'PAID': { bg: '#dcfce7', color: '#166534' },
  'PARTIAL': { bg: '#fef9c3', color: '#854d0e' },
  'PENDING': { bg: '#fee2e2', color: '#991b1b' },
  'OVERDUE': { bg: '#fce7f3', color: '#9d174d' },
};

const PaymentRemainder = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate] = useState(fmt2Date(today));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId, token]);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData('SaleReportApi', `/PaymentRemainder/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const rows = res?.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch payment remainder report');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.CustomerName || '').toLowerCase().includes(q) ||
      (r.BillNo || '').toLowerCase().includes(q) ||
      (r.ContactNumber || '').toLowerCase().includes(q) ||
      (r.Status || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const grandTotals = useMemo(() => filtered.reduce((acc, r) => ({
    billAmount: acc.billAmount + (parseFloat(r.BillAmount) || 0),
    paidAmount: acc.paidAmount + (parseFloat(r.PaidAmount) || 0),
    balanceAmount: acc.balanceAmount + (parseFloat(r.BalanceAmount) || 0),
  }), { billAmount: 0, paidAmount: 0, balanceAmount: 0 }), [filtered]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'PaymentRemainder', 0);
    if (!filtered.length) return;
    const headers = ['#', 'Bill No', 'Bill Date', 'Due Date', 'Customer', 'Contact',
      'Bill Amount', 'Paid Amount', 'Balance Amount', 'Bill Age Days', 'Overdue Days', 'Status'];
    const rows = filtered.map((r, i) => [
      i + 1, r.BillNo, fmtDate(r.BillDate), fmtDate(r.PaymentDueDate),
      r.CustomerName, r.ContactNumber,
      r.BillAmount, r.PaidAmount, r.BalanceAmount,
      r.BillAgeDays, r.OverDueDays, r.Status,
    ]);

      await exportStyledExcel({
          reportTitle: 'PAYMENT REMAINDER REPORT',
          sheetName: 'Payment Remainder',
          fileName: `PaymentRemainder_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: headers,
          rows,
          totalsRow: ['', '', '', '', '', 'Totals', '',
        fmtN(grandTotals.billAmount), fmtN(grandTotals.paidAmount), fmtN(grandTotals.balanceAmount), '', '', ''],
          columnWidths: [4, 8, 14, 12, 12, 26, 14, 14, 14, 14, 12, 12, 10],
      });
  };

  const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #9fa8da', color: '#fff', background: PRIMARY_MID };
  const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading payment remainder report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Payment Remainder
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Outstanding Receivable</span>
          </span>
          
          <div style={{ flex: 1, minWidth: 4 }} />
          <input
            placeholder="Search customer / bill..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 180, outline: 'none' }}
          />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Meta */}
        {fetched && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
            {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} records
            {search && ` (filtered from ${data.length})`}
            &nbsp;|&nbsp; Balance: <strong style={{ color: '#b91c1c' }}>{fmtN(grandTotals.balanceAmount)}</strong>
          </div>
        )}

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 140px)' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 400, width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                {/* <th style={{ ...thBase, textAlign: 'center' }}>Sale ID</th> */}
                <th style={{ ...thBase, textAlign: 'left' }}>Bill No</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Due Date</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 180 }}>Customer</th>
                <th style={{ ...thBase, textAlign: 'left' }}>Contact</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Bill Amount</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Paid Amount</th>
                <th style={{ ...thBase, textAlign: 'right', background: '#0f4c81' }}>Balance</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bill Age</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Overdue Days</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click Refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const statusStyle = STATUS_COLORS[(r.Status || '').toUpperCase()] || { bg: '#f1f5f9', color: '#475569' };
                  const isOverdue = (r.OverDueDays || 0) > 0;
                  const rowBg = i % 2 === 0 ? '#fff' : '#e8eaf6';
                  return (
                    <tr key={`${r.SaleId}-${i}`} style={{ background: isOverdue ? (i % 2 === 0 ? '#fff8f8' : '#fff0f0') : rowBg }}>
                      <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{r.RN || i + 1}</td>
                      {/* <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600, color: PRIMARY }}>{r.SaleId}</td> */}
                      <td style={{ ...tdBase, fontWeight: 500, color: PRIMARY }}>{r.BillNo || '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.BillDate)}</td>
                      <td style={{ ...tdBase, textAlign: 'center', color: isOverdue ? '#dc2626' : '#374151', fontWeight: isOverdue ? 600 : 400 }}>{fmtDate(r.PaymentDueDate)}</td>
                      <td style={{ ...tdBase, fontWeight: 500 }}>{r.CustomerName}</td>
                      <td style={{ ...tdBase, color: '#0277bd' }}>{r.ContactNumber || '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtN(r.BillAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'right', color: '#059669' }}>{fmtN(r.PaidAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fff5f5' }}>{fmtN(r.BalanceAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'center', color: r.BillAgeDays > 60 ? '#dc2626' : r.BillAgeDays > 30 ? '#d97706' : '#374151', fontWeight: r.BillAgeDays > 30 ? 600 : 400 }}>{r.BillAgeDays ?? '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'center', color: isOverdue ? '#dc2626' : '#374151', fontWeight: isOverdue ? 700 : 400 }}>{r.OverDueDays ?? '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: statusStyle.bg, color: statusStyle.color }}>
                          {r.Status || '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: '#e8eaf6', fontWeight: 700 }}>
                  <td colSpan={6} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(grandTotals.billAmount)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtN(grandTotals.paidAmount)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fecaca' }}>{fmtN(grandTotals.balanceAmount)}</td>
                  <td colSpan={3} style={{ ...tdBase }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentRemainder;



